import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "./LanguageContext";
import { api } from "../lib/http";

/* ================= CONFIG ================= */
const API_BASE_URL = "https://shopaholics.ge/api";

/* ================= THEME ================= */
const UI = {
  text: "#111827",
  dim: "#6b7280",
  line: "rgba(0,0,0,.08)",
  subtle: "#f7f7f8",
  bg: "#ffffff",
  brand: "#a15a5a",
  brandDark: "#8e4a4a",
  card: "#ffffff",
  shadow: "0 8px 24px rgba(0,0,0,.06)",
  shadowHover: "0 12px 30px rgba(0,0,0,.08)",
};

const RAD = { md: "12px", lg: "16px", xl: "20px", pill: "999px" };

/* ================= ANIMS ================= */
const fadeInUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const spin = keyframes`to{transform:rotate(360deg)}`;

/* ================= LAYOUT ================= */
const Wrap = styled.section`
  max-width: 1280px;
  margin: 20px auto 40px;
  padding: 0 clamp(10px,4vw,16px);
  display: grid;
  gap: 18px;
`;

const TrioGrid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: 1.15fr 1fr 1fr;

  @media (max-width: 1100px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 800px)  { grid-template-columns: 1fr; }
`;

/* ================= CARDS / COMMON ================= */
const Card = styled.div`
  background: ${UI.card};
  border: 1px solid ${UI.line};
  border-radius: ${RAD.xl};
  box-shadow: ${UI.shadow};
  padding: 18px;
  min-height: 260px;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
  animation: ${fadeInUp} .18s ease;

  &:hover { transform: translateY(-2px); box-shadow: ${UI.shadowHover}; border-color: rgba(0,0,0,.12); }

  @media (max-width: 800px){ border-radius: ${RAD.lg}; }
`;

const SectionTitle = styled.h3`
  margin: 2px 0 10px 0;
  font-size: 1.18rem;
  font-weight: 900;
  letter-spacing: .2px;
  color: ${UI.text};
`;

const TitleDivider = styled.hr`
  height: 2px;
  border: 0;
  background: linear-gradient(90deg, ${UI.brand}, transparent 70%);
  margin: 6px 0 12px 0;
`;

const Text = styled.p`
  margin: 0;
  color: ${UI.dim};
  line-height: 1.65;
  font-size: .98rem;
`;

/* ================= SECTION 1 — CULTURE ================= */
const CultureCard = styled(Card)`
  display: grid;
  grid-template-rows: auto auto 1fr;
  gap: 8px;
`;

/* ================= SECTION 2 — GUIDE ================= */
const GuideWrap = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
`;

const GuideCard = styled.div`
  border: 1px solid ${UI.line};
  border-radius: ${RAD.lg};
  background: #fff;
  padding: 14px;
  position: relative;
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;

  &:hover{ transform: translateY(-2px); box-shadow: ${UI.shadowHover}; border-color: rgba(0,0,0,.12); }

  &::before{
    content:"";
    position:absolute; right:-60px; top:-60px;
    width: 180px; height: 180px; border-radius: 50%;
    background: radial-gradient(closest-side, rgba(161,90,90,.12), transparent 70%);
    pointer-events: none;
  }
`;

const GuideTitle = styled.h4`
  margin: 0 0 6px 0;
  font-size: 1rem; color: ${UI.text}; font-weight: 900; letter-spacing:.2px;
`;

const PillBtn = styled.button`
  appearance: none;
  border: 1px solid ${UI.brand};
  background: ${UI.brand};
  color: #fff;
  font-weight: 900;
  letter-spacing: .2px;
  border-radius: ${RAD.pill};
  height: 36px; padding: 0 14px;
  cursor: pointer;
  transition: transform .15s ease, filter .15s ease, box-shadow .15s ease;
  &:hover{ transform: translateY(-1px); filter: brightness(.96); box-shadow: ${UI.shadow}; }
`;

const GhostBtn = styled(PillBtn)`
  background: #fff; color: ${UI.brand};
  border-color: ${UI.line};
`;

/* Modal */
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.35); z-index: 3010;
  display: grid; place-items: center;
  animation: ${fadeInUp} .18s ease;
`;
const Modal = styled.div`
  width: min(680px, 92vw);
  background: #fff; border: 1px solid ${UI.line};
  border-radius: ${RAD.xl}; box-shadow: 0 20px 60px rgba(0,0,0,.2);
  padding: 18px;
`;
const ModalHead = styled.div`display:flex;justify-content:space-between;align-items:center; gap:12px;`;
const ModalTitle = styled.h4`margin:0;font-size:1.05rem;font-weight:900;color:${UI.text};`;
const CloseX = styled.button`
  width: 38px; height: 38px; border-radius: 10px; border: 1px solid ${UI.line};
  background: #fff; cursor: pointer;
  &:hover{ box-shadow: ${UI.shadow}; transform: translateY(-1px); }
`;
const ModalBody = styled.div`margin-top:10px;color:${UI.dim};line-height:1.65;`;

/* ================= SECTION 3 — AROMA PICKER (DB) ================= */
const PickerCard = styled(Card)`
  display: grid; gap: 10px;
`;

const SearchRow = styled.div`
  display: flex; gap: 10px; align-items: center;
  background: #fff; border: 1px solid ${UI.line}; border-radius: ${RAD.pill};
  padding: 8px 12px;
  input{
    flex: 1; border: 0; outline: 0; font-size: .98rem; background: transparent; color:${UI.text};
  }
`;

const Chips = styled.div`
  display:flex; flex-wrap:wrap; gap:8px;
  max-height: 180px; overflow:auto;
  padding-right: 4px;
  &::-webkit-scrollbar{ width: 8px; }
  &::-webkit-scrollbar-thumb{ background: ${UI.brand}; border-radius: 8px; }
`;

const Chip = styled.button`
  display:inline-flex; align-items:center; gap:8px;
  height: 34px; padding: 0 12px; border-radius: ${RAD.pill};
  border: 1px solid ${UI.line}; background: #fff; cursor: pointer; font-weight: 800;
  transition: transform .15s ease, box-shadow .15s ease, background .15s ease, color .15s ease, border-color .15s ease;

  &:hover{ transform: translateY(-1px); box-shadow: ${UI.shadow}; }

  ${({$active}) => $active && css`
    background: ${UI.brand};
    border-color: ${UI.brand};
    color: #fff;
  `}
`;

const SelectedRow = styled.div`
  display:flex; flex-wrap:wrap; gap:8px; margin-top: 6px;
`;

const SelectedBadge = styled.span`
  display:inline-flex; align-items:center; gap:6px;
  height: 30px; padding: 0 10px; border-radius: ${RAD.pill};
  border: 1px solid ${UI.line}; background: ${UI.subtle}; font-weight: 800; font-size: .9rem;
`;

const Actions = styled.div`
  display:flex; gap:8px; justify-content: flex-end; margin-top: 8px;
`;

const Spinner = styled.div`
  width: 40px; height: 40px; border-radius: 50%;
  border: 4px solid rgba(0,0,0,.1); border-top-color: ${UI.brand};
  animation: ${spin} 1s linear infinite;
  margin: 8px auto;
`;

const TARGET_ROUTE = "/products";

/* ================= HELPERS ================= */
function extractAromas(perfumes) {
  const set = new Set();
  for (const p of perfumes || []) {
    const raw = p?.aroma_en || p?.aroma || "";
    if (!raw) continue;
    raw.split(",").map(s => s.trim()).filter(Boolean).forEach(v => set.add(v));
  }
  return Array.from(set).sort((a,b)=>a.localeCompare(b));
}

/* ================= COPY (KA/EN) ================= */
const copy = {
  ka: {
    cultureTitle: "პერფიუმის კულტურა",
    cultureText:
      "სუნამო მხოლოდ სურნელი არაა — ის მოგზაურობაა მოგონებებსა და ემოციებში. სწორად შერჩეული არომატი ქმნის პირველ შთაბეჭდილებას და გახსენებს განსაკუთრებულ მომენტებს.",
    guide1Title: "როგორ ავარჩიოთ არომატი?",
    guide1Body:
      `
      შეადარეთ ზედა/გულის/საბაზისო ნოტები. ვიზიტზე გამოსცადეთ 2-3 სუნამო 2-3 წუთიანი ინტერვალით. შეისხურეთ თბილ წერტილებზე (მაჯები, იდაყვის შიდა მხარე). ნუ გამოიტანთ დასკვნას პირველი 30 წამის განმავლობაში - გააჩერეთ 15-30 წუთი.
      `,
    guide2Title: "სუნამოს შენახვის წესები",
    guide2Body:
      `
      იდეალურია: 15–21°C, მშრალ და ბნელ ადგილას. მოარიდეთ მზის სხივებს და ტენიანობას. მჭიდროდ დაახურეთ თავსახური. საუკეთესო შედეგისთვის, გახსნილი ბოთლი გამოიყენეთ დაახლოებით 24 თვის განმავლობაში.
      `,
    guide3Title: "რომელი ნოტები გიხდება?",
    guide3Body:
      `სისუფთავე/მინიმალიზმი — ციტრუსი/არომატული. ელეგანტურობა — ხისებრი/ჭაჭრისიანი. რომანტიკა — ყვავილოვანი/პუდრული. ავანტიურა — აღმოსავლური/სუნელოვანი.`,
    learnMore: "გაიგე მეტი",
    pickerTitle: "იპოვე შენი სურნელი",
    pickerLead: "აირჩიეთ რამდენიმე არომატის ოჯახი და ჩვენ გაჩვენებთ კოლექციებს, რომლებიც თქვენს გემოვნებას შეესაბამება.",
    searchPlaceholder: "ძებნა არომატებში… (მაგ: Citrus, Woody, Floral)",
    notFound: "არომატი ვერ მოიძებნა.",
    clear: "გასუფთავება",
    go: "გადასვლა",
  },
  en: {
    cultureTitle: "The Culture of Perfume",
    cultureText:
      "Perfume isn’t just a scent — it’s a journey through memories and emotions. The right aroma shapes first impressions and brings special moments back to life.",
    guide1Title: "How to choose a fragrance?",
    guide1Body:
      `Compare top/heart/base notes. Test 2–3 perfumes per visit with 2–3 minutes in between. Spray on warm spots (wrists, inner elbow). Don’t judge in the first 30 seconds — let it settle for 15–30 minutes.`,
    guide2Title: "Storage best practices",
    guide2Body:
      `Ideal: 15–21°C, dry and dark place. Avoid sunlight and humidity. Keep the cap tight. For best results, use an opened bottle within ~24 months.`,
    guide3Title: "Which notes suit you?",
    guide3Body:
      `Clean/minimal — citrus/aromatic. Elegant — woody/chypre. Romantic — floral/powdery. Adventurous — oriental/spicy.`,
    learnMore: "Learn more",
    pickerTitle: "Find Your Scent",
    pickerLead: "Pick a few aroma families and we’ll show collections that match your taste.",
    searchPlaceholder: "Search aromas… (e.g., Citrus, Woody, Floral)",
    notFound: "No aromas found.",
    clear: "Clear",
    go: "Explore",
  }
};

/* ================= MAIN COMPONENT ================= */
export default function HomePerfumeShowcase() {
  const navigate = useNavigate();
  const { language } = useLanguage(); // expects "ka" or "en"
  const t = language === "en" ? copy.en : copy.ka;

  // modal (Guide)
  const [modal, setModal] = useState(null);

  // aromas from DB
  const [loading, setLoading] = useState(true);
  const [allAromas, setAllAromas] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`${API_BASE_URL}/get_perfumes.php`);
        if (!mounted) return;
        setAllAromas(extractAromas(data));
      } catch (e) {
        console.error("Error fetching perfumes for aromas:", e);
        if (!mounted) setAllAromas([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? allAromas.filter(a => a.toLowerCase().includes(q)) : allAromas;
  }, [allAromas, query]);

  const toggleSelect = (aroma) => {
    setSelected((prev) =>
      prev.includes(aroma) ? prev.filter(a => a !== aroma) : [...prev, aroma]
    );
  };

  const clearAll = () => setSelected([]);

  const goToCategoryWithFilters = () => {
    if (!selected.length) return;
    const qp = new URLSearchParams({ aromas: selected.join(",") }).toString();
    navigate(`${TARGET_ROUTE}?${qp}`, {
      state: { selectedAromas: selected }
    });
  };

  return (
    <Wrap>
      <TrioGrid>
        {/* ============ 1) CULTURE ============ */}
        <CultureCard>
          <SectionTitle>{t.cultureTitle}</SectionTitle>
          <TitleDivider />
          <Text>{t.cultureText}</Text>
        </CultureCard>

        {/* ============ 2) GUIDE ============ */}
        <GuideWrap>
          <GuideCard>
            <GuideTitle>{t.guide1Title}</GuideTitle>
            <Text>{language === "en"
              ? "Start from the note pyramid: citrus for energy, woody for elegance, floral for romance."
              : "დაიწყე ნოტების პირამიდით: ციტრუსი ენერგიისთვის, ხისებრი ელეგანტურობისთვის, ყვავილოვანი — რომანტიკისთვის."}
            </Text>
            <div style={{ marginTop: 10 }}>
              <PillBtn onClick={()=>setModal({ title: t.guide1Title, body: t.guide1Body })}>
                {t.learnMore}
              </PillBtn>
            </div>
          </GuideCard>

          <GuideCard>
            <GuideTitle>{t.guide2Title}</GuideTitle>
            <Text>{language === "en"
              ? "Keep in a cool, dark place away from sunlight and humidity."
              : "შეინახე გრილ და ბნელ ადგილას, მზის და ტენის გარეშე."}
            </Text>
            <div style={{ marginTop: 10 }}>
              <PillBtn onClick={()=>setModal({ title: t.guide2Title, body: t.guide2Body })}>
                {t.learnMore}
              </PillBtn>
            </div>
          </GuideCard>

          <GuideCard>
            <GuideTitle>{t.guide3Title}</GuideTitle>
            <Text>{language === "en"
              ? "Citrus for everyday freshness, woody for refined evenings, floral for warm, soft moments."
              : "ციტრუსი — ყოველდღიური სისუფთავე; ხისებრი — დახვეწილი საღამო; ყვავილოვანი — თბილი, ნაზი მომენტები."}
            </Text>
            <div style={{ marginTop: 10 }}>
              <PillBtn onClick={()=>setModal({ title: t.guide3Title, body: t.guide3Body })}>
                {t.learnMore}
              </PillBtn>
            </div>
          </GuideCard>
        </GuideWrap>

        {/* ============ 3) AROMA PICKER (DB) ============ */}
        <PickerCard>
          <SectionTitle>{t.pickerTitle}</SectionTitle>
          <TitleDivider />
          <Text>{t.pickerLead}</Text>

          <SearchRow style={{ marginTop: 10 }}>
            <input
              placeholder={t.searchPlaceholder}
              value={query}
              onChange={(e)=>setQuery(e.target.value)}
              aria-label={t.searchPlaceholder}
            />
          </SearchRow>

          {loading ? (
            <Spinner />
          ) : (
            <>
              <Chips>
                {filtered.map((a) => (
                  <Chip key={a} $active={selected.includes(a)} onClick={()=>toggleSelect(a)}>
                    {a}
                  </Chip>
                ))}
                {!filtered.length && <Text style={{padding:"8px 2px"}}>{t.notFound}</Text>}
              </Chips>

              {!!selected.length && (
                <>
                  <SelectedRow>
                    {selected.map(s => <SelectedBadge key={s}>#{s}</SelectedBadge>)}
                  </SelectedRow>
                  <Actions>
                    <GhostBtn onClick={clearAll}>{t.clear}</GhostBtn>
                    <PillBtn onClick={goToCategoryWithFilters}>{t.go}</PillBtn>
                  </Actions>
                </>
              )}

              {!selected.length && (
                <Actions>
                  <GhostBtn onClick={clearAll}>{t.clear}</GhostBtn>
                  <PillBtn disabled style={{ opacity:.6, cursor:"not-allowed" }}>{t.go}</PillBtn>
                </Actions>
              )}
            </>
          )}
        </PickerCard>
      </TrioGrid>

      {/* ===== Modal ===== */}
      {modal && (
        <Overlay onClick={()=>setModal(null)}>
          <Modal onClick={(e)=>e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{modal.title}</ModalTitle>
              <CloseX onClick={()=>setModal(null)} aria-label="close">✕</CloseX>
            </ModalHead>
            <ModalBody>
              {modal.body.split("\n").map((line, idx)=> <p key={idx} style={{margin:"6px 0"}}>{line}</p>)}
            </ModalBody>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
}
